import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { MapPin, Phone, Mail, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function Contact() {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      
      setSubmitStatus({ type: 'success', message: t('contact.success') });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ type: 'error', message: t('contact.error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: t('contact.addressLabel'),
      value: ['Rua da Estrada Nova, 203', '4435-234 Rio Tinto | Portugal']
    },
    {
      icon: Phone,
      label: t('contact.phoneLabel'),
      value: ['221 450 880']
    },
    {
      icon: Phone,
      label: t('contact.emergencyLabel'),
      value: ['92 850 1136']
    },
    {
      icon: Mail,
      label: t('contact.emailLabel'),
      value: ['info@7x7travel.pt']
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-[#00D4FF]/20 text-[#00D4FF] rounded-full text-sm font-medium mb-6">
              7x7 Travel
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('contact.title')}
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              className="bg-white rounded-3xl shadow-xl p-8 md:p-10"
              {...fadeInUp}
            >
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('contact.formTitle')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">{t('contact.name')} *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">{t('contact.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone">{t('contact.phone')}</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="mt-2 h-12"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">{t('contact.subject')} *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="contact-subject"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">{t('contact.message')} *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="mt-2 min-h-[150px]"
                    data-testid="contact-message"
                  />
                </div>

                {/* Submit Status */}
                {submitStatus && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-xl flex items-center gap-3 ${
                      submitStatus.type === 'success'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                    }`}
                    data-testid="contact-status"
                  >
                    {submitStatus.type === 'success' ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <AlertCircle className="w-5 h-5" />
                    )}
                    {submitStatus.message}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full py-6 text-lg font-semibold"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t('contact.sending')}
                    </>
                  ) : (
                    t('contact.send')
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl font-bold text-[#0F172A] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('contact.infoTitle')}
              </h2>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-sm"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#00D4FF] flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0F172A] mb-1">{info.label}</h3>
                      {info.value.map((v, i) => (
                        <p key={i} className="text-slate-600">{v}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map Placeholder */}
              <div className="mt-8 aspect-video rounded-2xl overflow-hidden bg-slate-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3003.8451831442254!2d-8.558611!3d41.178611!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDEwJzQzLjAiTiA4wrAzMyczMS4wIlc!5e0!3m2!1spt-PT!2spt!4v1600000000000!5m2!1spt-PT!2spt"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="7x7 Travel Location"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white" data-testid="contact-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center p-12 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] rounded-3xl"
            {...fadeInUp}
          >
            <Clock className="w-12 h-12 text-[#00D4FF] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Assistência 24/7
            </h2>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              Estamos disponíveis 24 horas por dia, 7 dias por semana para apoiar a sua agência.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:221450880" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-full transition-colors">
                <Phone className="w-5 h-5" />
                221 450 880
              </a>
              <a href="tel:928501136" className="flex items-center gap-2 bg-[#00D4FF] hover:bg-[#00D4FF]/90 text-[#0F172A] px-6 py-3 rounded-full transition-colors font-semibold">
                <Phone className="w-5 h-5" />
                92 850 1136 (24H)
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
